import type { FlowEventContext } from "conversion_flows/types/flowEvent"
import type {
  LeadInput,
  ValuationConsentInput,
  ExtraQuestionInput,
} from "conversion_flows/types/scalars"
import type { WebsiteScriptData } from "conversion_flows/types/websiteScriptData"
import type ModuleTypes from "website_script/constants/ModuleTypes"
import type { BuildUrlDataOutput } from "website_script/lib/buildModuleUrlData"
import { clickTrackingData } from "widgets/TradeInValuations/Utility/clickTrackingData"

export function assertIsTypename<T extends { __typename: string }>(
  typename: string
) {
  return function (entity: { __typename?: string }): asserts entity is T {
    if (entity.__typename !== typename)
      throw Error(
        `Type mismatch: expecting ${entity.__typename} to be ${typename}`
      )
  }
}

/** Data coming in from the flow schema form */
export type SchemaFormLead = {
  name: string
  email: string
  phone_number?: string
  comments?: string
  [consentId: `consent-${number}`]: boolean
  [extraQuestionName: `eq-${string}`]: string
}

/** Shape consents from schema form into objects used for making the request */
const extractConsentsFromSchemaForm = (
  formData: SchemaFormLead
): LeadInput["valuationConsents"] => {
  const mappedConsents = Object.entries(formData).map(([field, value]) => {
    if (!field.startsWith("consent-")) return

    const consentIdString = field.split("consent-")[1]
    if (!consentIdString) return

    if (typeof value === "string") return

    return { tradeInConsentId: consentIdString, agreed: value }
  })

  return mappedConsents.filter(
    (consent): consent is ValuationConsentInput => !!consent
  )
}

/** Shape answers to extra questions from schema form into objects used for making the request */
const extractExtraQuestionsFromSchemaForm = (
  formData: SchemaFormLead
): LeadInput["extraQuestions"] => {
  const answers = Object.entries(formData).map(([field, value]) => {
    if (!field.startsWith("eq-")) return

    const name = field.split("eq-")[1]
    if (!name) return

    return { name, answer: value }
  })

  return answers.filter((answer): answer is ExtraQuestionInput => !!answer)
}

/** Shape data from schema form so it can be used for the request */
export const leadInputFromSchemaForm = (
  formData: SchemaFormLead
): Pick<
  LeadInput,
  | "buyerEmail"
  | "buyerName"
  | "buyerPhoneNumber"
  | "buyerComments"
  | "valuationConsents"
  | "extraQuestions"
> => {
  return {
    buyerEmail: formData.email,
    buyerName: formData.name,
    buyerPhoneNumber: formData.phone_number || "",
    buyerComments: formData.comments,
    valuationConsents: extractConsentsFromSchemaForm(formData),
    extraQuestions: extractExtraQuestionsFromSchemaForm(formData),
  }
}

// Tracking
type FlowContextInput = {
  completionSource?: string
  requiresEmailVerification?: boolean
  moduleType: ModuleTypes
  websiteScriptData: WebsiteScriptData
  departmentId: string | null
  carId: string | null
  checkLeadVerificationUrl?: string
}

export const getFlowContext = ({
  completionSource,
  requiresEmailVerification,
  moduleType,
  websiteScriptData,
  departmentId,
  carId,
  checkLeadVerificationUrl,
}: FlowContextInput): FlowEventContext => ({
  flowType: moduleType,
  departmentId,
  carId,
  carUrl: websiteScriptData.carUrl,
  completionSource: completionSource,
  customerId: websiteScriptData.cId,
  clickTrackingData: clickTrackingData(websiteScriptData.cId),
  dateTime: new Date().toISOString(),
  dataFromSource: websiteScriptData.dataFromSource,
  leadFlowId: websiteScriptData.leadFlowId,
  requiresEmailVerification,
  checkLeadVerificationUrl,
})

export const getFlowContextFromModuleData = ({
  moduleData,
  locale,
}: {
  moduleData: BuildUrlDataOutput
  locale: string
}): FlowEventContext =>
  getFlowContext({
    moduleType: moduleData.moduleType,
    websiteScriptData: {
      cId: moduleData.cId,
      carUrl: moduleData.refUrl,
      depId: moduleData.depId ?? undefined,
      leadFlowId: moduleData.leadFlowId?.toString(),
      locale,
      dataFromSource: moduleData.data_from_source,
      appHost: moduleData.host,
    },
    departmentId: moduleData.depId ?? null,
    carId: moduleData.au_car_id ?? null,
  })

export const getValuationConsents = (
  valuationConsents: ValuationConsentInput[]
) =>
  valuationConsents.reduce((attributes, consent, index) => {
    attributes[String(index)] = {
      agreed: consent.agreed,
      tradeInConsentId: Number(consent.tradeInConsentId),
    }
    return attributes
  }, {} as Record<string, { agreed: boolean; tradeInConsentId: number }>)
